import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bell,
  CalendarDays,
  Copy,
  Download,
  Eye,
  LayoutDashboard,
  LogOut,
  Menu,
  Phone,
  RefreshCw,
  ReceiptText,
  Search,
  Share2,
  TrendingUp,
  Upload,
  UserCheck,
  UserPlus,
  Users,
  Trash2,
  Wallet,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";

function AgentPanel() {
  const navigate = useNavigate();
  const agent = useMemo(() => JSON.parse(localStorage.getItem("agent") || "null"), []);
  const [data, setData] = useState({ agent: null, totals: {}, clients: [], pending: { deposits: [], withdrawals: [] } });
  const [view, setView] = useState(() => localStorage.getItem("agent_active_view") || "dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [showDeposit, setShowDeposit] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [depositForm, setDepositForm] = useState({ user_id: "", amount: "", remark: "", screenshot: null });
  const [busy, setBusy] = useState(false);
  const [transactionFilters, setTransactionFilters] = useState({
    status: "all",
    date_from: new Date().toISOString().slice(0, 10),
    date_to: new Date().toISOString().slice(0, 10),
  });
  const [transactionData, setTransactionData] = useState({ deposits: [], withdrawals: [] });
  const [leadFilters, setLeadFilters] = useState({
    q: "",
    status: "pending",
    date_from: new Date().toISOString().slice(0, 10),
    date_to: new Date().toISOString().slice(0, 10),
  });
  const [leadData, setLeadData] = useState({ rows: [], options: [], summary: {} });
  const [leadCallStats, setLeadCallStats] = useState({ rows: [], totals: {} });
  const [profileState, setProfileState] = useState({
    open: false,
    loading: false,
    userId: null,
    tab: "dw",
    date_from: new Date().toISOString().slice(0, 10),
    date_to: new Date().toISOString().slice(0, 10),
    data: null,
  });

  const load = useCallback(async () => {
    if (!agent?.id) return;
    try {
      const res = await API.get(`/agents/${agent.id}/overview`);
      setData(res.data || { agent: null, totals: {}, clients: [], pending: { deposits: [], withdrawals: [] } });
    } catch (err) {
      console.log(err);
    }
  }, [agent?.id]);

  useEffect(() => {
    if (!agent?.id) {
      navigate("/agent-login");
      return;
    }
    if (typeof window !== "undefined" && window.innerWidth <= 900) {
      setSidebarOpen(false);
    }
    load();
  }, [agent, load, navigate]);

  const openView = (nextView) => {
    setView(nextView);
    localStorage.setItem("agent_active_view", nextView);
    if (typeof window !== "undefined" && window.innerWidth <= 900) {
      setSidebarOpen(false);
    }
  };

  const clients = useMemo(() => data.clients || [], [data.clients]);
  const filteredClients = useMemo(() => {
    const term = query.trim().toLowerCase();
    return clients.filter((client) => {
      const status = String(client.status || "Active").toLowerCase();
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesSearch = !term || [client.name, client.phone, client.id].some((value) => String(value || "").toLowerCase().includes(term));
      return matchesStatus && matchesSearch;
    });
  }, [clients, query, statusFilter]);

  const topUsers = useMemo(() => {
    return [...clients]
      .sort((a, b) => Number(b.deposits || 0) - Number(a.deposits || 0))
      .slice(0, 5);
  }, [clients]);

  const todayUsers = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return clients.filter((client) => String(client.created_at || "").slice(0, 10) === today).length;
  }, [clients]);

  const createUser = async () => {
    try {
      await API.post(`/agents/${agent.id}/users`, form);
      setForm({ name: "", phone: "" });
      setShowCreate(false);
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to create client");
    }
  };

  const createDeposit = async () => {
    if (!depositForm.user_id || Number(depositForm.amount || 0) <= 0) {
      alert("Select player and enter amount");
      return;
    }
    try {
      setBusy(true);
      const payload = new FormData();
      payload.append("user_id", depositForm.user_id);
      payload.append("amount", depositForm.amount);
      payload.append("remark", depositForm.remark || "Agent deposit request");
      if (depositForm.screenshot) payload.append("screenshot", depositForm.screenshot);
      await API.post(`/agents/${agent.id}/deposits`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDepositForm({ user_id: "", amount: "", remark: "", screenshot: null });
      setShowDeposit(false);
      await load();
      alert("Deposit request sent to admin");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to create deposit request");
    } finally {
      setBusy(false);
    }
  };

  const shareLink = async () => {
    const code = data?.agent?.referral_code;
    const url = `${window.location.origin}/login?tab=register&ref=${encodeURIComponent(code || "")}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Matka Kings Agent", text: "Join using my agent link", url });
      } else {
        await navigator.clipboard.writeText(url);
        alert("Referral link copied");
      }
    } catch (_) {}
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(String(data?.agent?.referral_code || ""));
      alert("Referral code copied");
    } catch (_) {}
  };

  const logout = () => {
    localStorage.removeItem("agent");
    localStorage.removeItem("agent_active_view");
    navigate("/agent-login", { replace: true });
  };

  const loadTransactions = useCallback(async () => {
    if (!agent?.id) return;
    try {
      const params = new URLSearchParams();
      if (transactionFilters.status && transactionFilters.status !== "all") params.set("status", transactionFilters.status);
      if (transactionFilters.date_from && transactionFilters.date_to) {
        params.set("date_from", transactionFilters.date_from);
        params.set("date_to", transactionFilters.date_to);
      }
      const res = await API.get(`/agents/${agent.id}/transactions${params.toString() ? `?${params.toString()}` : ""}`);
      setTransactionData(res.data || { deposits: [], withdrawals: [] });
    } catch (err) {
      console.log(err);
    }
  }, [agent?.id, transactionFilters.date_from, transactionFilters.date_to, transactionFilters.status]);

  const loadLeads = useCallback(async () => {
    if (!agent?.id) return;
    try {
      const params = new URLSearchParams();
      if (leadFilters.q) params.set("q", leadFilters.q);
      if (leadFilters.status && leadFilters.status !== "all") params.set("status", leadFilters.status);
      const res = await API.get(`/agents/${agent.id}/leads${params.toString() ? `?${params.toString()}` : ""}`);
      setLeadData(res.data || { rows: [], options: [], summary: {} });
    } catch (err) {
      console.log(err);
    }
  }, [agent?.id, leadFilters.q, leadFilters.status]);

  const loadLeadCallStats = useCallback(async () => {
    if (!agent?.id) return;
    try {
      const params = new URLSearchParams();
      if (leadFilters.date_from) params.set("date_from", leadFilters.date_from);
      if (leadFilters.date_to) params.set("date_to", leadFilters.date_to);
      const res = await API.get(`/agents/${agent.id}/lead-call-stats${params.toString() ? `?${params.toString()}` : ""}`);
      setLeadCallStats(res.data || { rows: [], totals: {} });
    } catch (err) {
      console.log(err);
    }
  }, [agent?.id, leadFilters.date_from, leadFilters.date_to]);

  const updateLead = (id, patch) => {
    setLeadData((current) => ({
      ...current,
      rows: (current.rows || []).map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  };

  const saveLeadCall = async (lead) => {
    if (!agent?.id || !lead?.id) return;
    try {
      await API.post(`/agents/${agent.id}/leads/${lead.id}/call`, {
        status: lead.status || "called",
        remark: lead.remark || "",
      });
      await loadLeads();
      await loadLeadCallStats();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save call");
    }
  };

  const deleteLead = async (lead) => {
    if (!agent?.id || !lead?.id) return;
    if (!window.confirm(`Delete ${lead.phone || "this number"} from your list?`)) return;
    try {
      await API.delete(`/agents/${agent.id}/leads/${lead.id}`);
      await loadLeads();
      await loadLeadCallStats();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to delete lead");
    }
  };

  const openProfile = useCallback(async (userId) => {
    if (!agent?.id || !userId) return;
    setProfileState((current) => ({ ...current, open: true, loading: true, userId, data: null }));
    try {
      const params = new URLSearchParams();
      if (profileState.date_from && profileState.date_to) {
        params.set("date_from", profileState.date_from);
        params.set("date_to", profileState.date_to);
      }
      const res = await API.get(`/agents/${agent.id}/users/${userId}/profile${params.toString() ? `?${params.toString()}` : ""}`);
      setProfileState((current) => ({ ...current, loading: false, data: res.data || null }));
    } catch (err) {
      console.log(err);
      setProfileState((current) => ({ ...current, loading: false, data: null }));
    }
  }, [agent?.id, profileState.date_from, profileState.date_to]);

  const refreshProfile = useCallback(async () => {
    if (!agent?.id || !profileState.userId) return;
    setProfileState((current) => ({ ...current, loading: true }));
    try {
      const params = new URLSearchParams();
      if (profileState.date_from && profileState.date_to) {
        params.set("date_from", profileState.date_from);
        params.set("date_to", profileState.date_to);
      }
      const res = await API.get(`/agents/${agent.id}/users/${profileState.userId}/profile${params.toString() ? `?${params.toString()}` : ""}`);
      setProfileState((current) => ({ ...current, loading: false, data: res.data || null }));
    } catch (err) {
      console.log(err);
      setProfileState((current) => ({ ...current, loading: false }));
    }
  }, [agent?.id, profileState.date_from, profileState.date_to, profileState.userId]);

  const activeCount = clients.filter((client) => String(client.status || "Active").toLowerCase() === "active").length;
  const inactiveCount = clients.filter((client) => String(client.status || "").toLowerCase() === "inactive").length;
  const blockedCount = clients.filter((client) => String(client.status || "").toLowerCase() === "blocked").length;
  const pendingCommission = Number(data?.agent?.pending_commission ?? data?.totals?.pending_commission ?? 0);

  useEffect(() => {
    if (view === "transactions") loadTransactions();
  }, [loadTransactions, view]);

  useEffect(() => {
    if (view === "leads") {
      loadLeads();
      loadLeadCallStats();
    }
  }, [loadLeadCallStats, loadLeads, view]);

  return (
    <div style={page}>
      <style>{agentPanelCss}</style>
      <header style={topbar} className="agent-topbar">
        <div style={topbarBrand}>
          <button type="button" style={sidebarToggleButton} onClick={() => setSidebarOpen((value) => !value)}>
            <Menu size={19} />
            <span>{sidebarOpen ? "Hide" : "Menu"}</span>
          </button>
          <img src="/brand-logo.png" alt="Matka Kings" style={topbarLogo} />
          <div>
          <h2 style={welcome}>Welcome, {data?.agent?.name || agent?.name || "Agent"}!</h2>
          <p style={muted}>Manage your affiliate network</p>
          </div>
        </div>
        <div style={topbarRight}>
          <TopMetric label="Total Users" value={data.totals?.clients || 0} />
          <TopMetric label="Pending Commission" value={formatMoney(pendingCommission)} green />
          <button type="button" style={bellButton}><Bell size={22} /><span style={dot} /></button>
          <button type="button" style={profileButton}>{getInitial(data?.agent?.name || "A")}</button>
          <button type="button" style={logoutButton} onClick={logout}><LogOut size={16} /> Logout</button>
        </div>
      </header>

      <main style={sidebarOpen ? main : mainFull} className="agent-main">
        {sidebarOpen && (
        <aside style={sidebar} className="agent-sidebar">
          <div style={sidebarHead}>
            <img src="/mk-logo192.png" alt="Matka Kings" style={sidebarLogo} />
            <div>
              <strong style={sidebarTitle}>Matka Kings</strong>
              <p style={sidebarText}>Agent control</p>
            </div>
            <button type="button" style={sidebarCloseButton} onClick={() => setSidebarOpen(false)} aria-label="Hide menu">×</button>
          </div>
          <div style={sidebarNav}>
            <button type="button" style={sideNavButton(view === "dashboard")} onClick={() => openView("dashboard")}><LayoutDashboard size={18} /> Dashboard</button>
            <button type="button" style={sideNavButton(view === "users")} onClick={() => openView("users")}><Users size={18} /> Users</button>
            <button type="button" style={sideNavButton(view === "leads")} onClick={() => openView("leads")}><Phone size={18} /> Lead Calls</button>
            <button type="button" style={sideNavButton(view === "transactions")} onClick={() => openView("transactions")}><ReceiptText size={18} /> Transactions</button>
          </div>
          <div style={sidebarFoot}>
            <strong style={sidebarAgentName}>{data?.agent?.name || agent?.name || "Agent"}</strong>
            <span style={sidebarAgentMeta}>{data?.agent?.phone || data?.agent?.email || "Active agent account"}</span>
            <button type="button" style={sidebarLogout} onClick={logout}><LogOut size={16} /> Log out</button>
          </div>
        </aside>
        )}

        {view === "dashboard" ? (
          <DashboardView
            data={data}
            topUsers={topUsers}
            todayUsers={todayUsers}
            onCreate={() => setShowCreate(true)}
            onDeposit={() => setShowDeposit(true)}
            onShare={shareLink}
            onCopy={copyCode}
            onUsers={() => setView("users")}
            onTransactions={() => setView("transactions")}
            onRefresh={load}
          />
        ) : view === "users" ? (
          <UsersView
            clients={filteredClients}
            total={clients.length}
            active={activeCount}
            inactive={inactiveCount}
            blocked={blockedCount}
            query={query}
            statusFilter={statusFilter}
            onQuery={setQuery}
            onStatus={setStatusFilter}
            onCreate={() => setShowCreate(true)}
            onProfile={openProfile}
          />
        ) : view === "leads" ? (
          <LeadsView
            rows={leadData.rows || []}
            options={leadData.options || []}
            summary={leadData.summary || {}}
            filters={leadFilters}
            onFilters={setLeadFilters}
            onSearch={loadLeads}
            callStats={leadCallStats}
            onStats={loadLeadCallStats}
            onUpdate={updateLead}
            onCall={saveLeadCall}
            onDelete={deleteLead}
          />
        ) : (
          <TransactionsView
            deposits={transactionData.deposits || []}
            withdrawals={transactionData.withdrawals || []}
            filters={transactionFilters}
            onFilters={setTransactionFilters}
          />
        )}
      </main>

      {showCreate && (
        <Modal title="Create New User" onClose={() => setShowCreate(false)} onSave={createUser}>
          <Field label="Name"><input style={input} value={form.name} onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))} /></Field>
          <Field label="Phone"><input style={input} value={form.phone} onChange={(e) => setForm((c) => ({ ...c, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))} /></Field>
        </Modal>
      )}

      {showDeposit && (
        <Modal title="Player Deposit Request" onClose={() => setShowDeposit(false)} onSave={createDeposit} saveLabel={busy ? "Sending..." : "Send Request"}>
          <Field label="Player">
            <select style={input} value={depositForm.user_id} onChange={(e) => setDepositForm((c) => ({ ...c, user_id: e.target.value }))}>
              <option value="">Select player</option>
              {clients.map((client) => <option key={client.id} value={client.id}>{client.name} - {client.phone}</option>)}
            </select>
          </Field>
          <Field label="Amount"><input style={input} value={depositForm.amount} onChange={(e) => setDepositForm((c) => ({ ...c, amount: e.target.value.replace(/[^\d.]/g, "") }))} /></Field>
          <Field label="Note"><input style={input} value={depositForm.remark} onChange={(e) => setDepositForm((c) => ({ ...c, remark: e.target.value }))} /></Field>
          <label style={fileButton}>
            <Upload size={16} /> {depositForm.screenshot ? depositForm.screenshot.name : "Upload Screenshot"}
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => setDepositForm((c) => ({ ...c, screenshot: e.target.files?.[0] || null }))} />
          </label>
        </Modal>
      )}

      {profileState.open ? (
        <AgentUserProfileModal
          state={profileState}
          onClose={() => setProfileState((current) => ({ ...current, open: false }))}
          onTab={(tab) => setProfileState((current) => ({ ...current, tab }))}
          onDate={(key, value) => setProfileState((current) => ({ ...current, [key]: value }))}
          onApply={refreshProfile}
        />
      ) : null}
    </div>
  );
}

function DashboardView({ data, topUsers, todayUsers, onCreate, onDeposit, onShare, onCopy, onUsers, onTransactions, onRefresh }) {
  const totals = data.totals || {};
  const pendingCommission = Number(data?.agent?.pending_commission ?? totals.pending_commission ?? 0);
  const paidCommission = Number(data?.agent?.paid_commission ?? totals.paid_commission ?? 0);
  const exactCommission = Number(data?.agent?.exact_commission ?? totals.exact_commission ?? pendingCommission + paidCommission);
  return (
    <section style={dashboard}>
      <div style={dashboardHeader} className="agent-dashboard-header">
        <div>
          <h1 style={pageTitle}>Dashboard <span style={liveBadge}>Live</span></h1>
          <p style={muted}>Welcome, {data?.agent?.name || "Agent"}</p>
        </div>
        <div style={dashboardTools}>
          <button type="button" style={todayCard}><UserPlus size={22} /> <span>Today's New Users</span><strong>{todayUsers}</strong></button>
          <button type="button" style={refreshButton} onClick={onRefresh}><RefreshCw size={24} /></button>
        </div>
      </div>

      <div style={statGrid} className="agent-stat-grid">
        <BigStat title="Total Users" value={totals.clients || 0} foot={`+${todayUsers} today`} color="#8b5cf6" icon={<Users size={30} />} />
        <BigStat title="Total Deposits" value={formatCompactMoney(totals.deposits)} foot="All time" color="#10b981" icon={<Download size={30} />} />
        <BigStat title="Commission Rate" value={`${Number(data?.agent?.commission_rate || 0)}%`} foot={`Total: ${formatMoney(exactCommission)}`} color="#f97316" icon={<span style={percentIcon}>%</span>} />
        <BigStat title="Pending Commission" value={formatMoney(pendingCommission)} foot={`Settled: ${formatMoney(paidCommission)}`} color="#2563eb" icon={<span style={dollarIcon}>$</span>} />
      </div>

      <div style={twoCol} className="agent-two-col">
        <section style={panel}>
          <PanelTitle icon={<TrendingUp size={22} />} title="Financial Summary" subtitle="Deposits, Withdrawals & Net" />
          <div style={moneyGrid}>
            <MoneyBox label="Deposits" value={`+${formatCompactMoney(totals.deposits)}`} tone="green" />
            <MoneyBox label="Withdrawals" value={`-${formatCompactMoney(totals.withdrawals)}`} tone="red" />
            <MoneyBox label="Net DMW" value={formatMoney(totals.dmw)} tone={Number(totals.dmw || 0) < 0 ? "red" : "green"} wide />
          </div>
        </section>

        <section style={panel}>
          <PanelTitle icon={<Wallet size={22} />} title="Quick Actions" subtitle="Common tasks" />
          <div style={actionsGrid}>
            <ActionCard title="Create User" subtitle="Add new user" color="#2563eb" bg="#eff6ff" onClick={onCreate} icon={<UserPlus size={30} />} />
            <ActionCard title="Referral Link" subtitle="Share & earn" color="#9333ea" bg="#faf5ff" onClick={onShare} icon={<Share2 size={30} />} />
            <ActionCard title="Deposit Request" subtitle="Upload screenshot" color="#059669" bg="#ecfdf5" onClick={onDeposit} icon={<Upload size={28} />} />
            <ActionCard title="Transactions" subtitle="View D/W history" color="#ea580c" bg="#fff7ed" onClick={onTransactions} icon={<Wallet size={30} />} />
            <button type="button" style={wideAction} onClick={onCopy}><Copy size={24} /> Copy Referral Code</button>
          </div>
        </section>
      </div>

      <section style={panel}>
        <PanelTitle icon={<Users size={22} />} title="Top Users by Volume" subtitle="Highest depositors" />
        <div style={topUserList} className="agent-top-list">
          {topUsers.map((client, index) => (
            <div key={client.id} style={topUserRow}>
              <span style={rankBadge(index)}>{index + 1}</span>
              <div style={{ flex: 1 }}>
                <strong>{client.name}</strong>
                <p>{client.phone}</p>
              </div>
              <div style={rightMoney}>
                <strong>{formatCompactMoney(client.deposits)}</strong>
                <span>Net: {formatCompactMoney(client.dmw)}</span>
              </div>
            </div>
          ))}
          {!topUsers.length && <p style={emptyText}>No users yet.</p>}
        </div>
        <button type="button" style={viewUsersButton} onClick={onUsers}>View All Users</button>
      </section>
    </section>
  );
}

function UsersView({ clients, total, active, inactive, blocked, query, statusFilter, onQuery, onStatus, onCreate, onProfile }) {
  return (
    <section style={dashboard}>
      <div style={dashboardHeader} className="agent-dashboard-header">
        <div>
          <h1 style={pageTitle}>My Users</h1>
          <p style={muted}>{total} total users in your network</p>
        </div>
        <button type="button" style={createUserButton} onClick={onCreate}><UserPlus size={18} /> Create New User</button>
      </div>

      <section style={searchPanel} className="agent-search-panel">
        <div style={largeSearch}>
          <Search size={22} color="#94a3b8" />
          <input value={query} onChange={(e) => onQuery(e.target.value)} placeholder="Search by name, username or mobile..." />
        </div>
        <div style={statusTabs} className="agent-status-tabs">
          <FilterButton label="All" count={total} active={statusFilter === "all"} onClick={() => onStatus("all")} />
          <FilterButton label="Active" count={active} active={statusFilter === "active"} onClick={() => onStatus("active")} />
          <FilterButton label="Inactive" count={inactive} active={statusFilter === "inactive"} onClick={() => onStatus("inactive")} />
          <FilterButton label="Blocked" count={blocked} active={statusFilter === "blocked"} onClick={() => onStatus("blocked")} />
          <button type="button" style={downloadButton}><Download size={20} /></button>
        </div>
      </section>

      <section style={userTablePanel}>
          <table style={userTable} className="agent-users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Balance</th>
              <th>Deposits</th>
              <th>Withdrawals</th>
              <th>DMW</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.id}>
                <td>
                  <div style={userCell}>
                    <span style={clientAvatar}>{getInitial(client.name)}</span>
                    <div>
                      <strong>{client.name}</strong>
                      <p>{client.name} - {client.phone}</p>
                    </div>
                  </div>
                </td>
                <td><strong>{formatMoney(client.balance)}</strong><p style={smallMuted}>W: {formatMoney(client.winnings)}</p></td>
                <td style={greenText}>{formatMoney(client.deposits)}</td>
                <td style={redText}>{formatMoney(client.withdrawals)}</td>
                <td style={Number(client.dmw || 0) < 0 ? redText : greenText}>{formatMoney(client.dmw)}</td>
                <td><span style={statusPill(client.status)}>{client.status || "Active"}</span></td>
                <td><button type="button" style={profileButtonSmall} onClick={() => onProfile(client.id)}><Eye size={16} /> Profile</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </section>
  );
}

function LeadsView({ rows, options, summary = {}, filters, onFilters, onSearch, callStats, onStats, onUpdate, onCall, onDelete }) {
  const statusOptions = options.filter((item) => item.type === "status" || item.type === "category");
  const languageOptions = options.filter((item) => item.type === "language");
  const quickRemarkOptions = statusOptions.slice(0, 8);
  const statusCounts = summary.status_counts || {};
  const inferFromRemark = (value) => {
    const text = String(value || "").trim().toLowerCase();
    const patch = {};
    if (!text) return patch;
    for (const option of options) {
      const label = String(option.label || "").trim();
      const normalizedLabel = label.toLowerCase();
      if (!normalizedLabel) continue;
      if (text === normalizedLabel || text.includes(normalizedLabel)) {
        if (option.type === "language") patch.language = label;
        if (option.type === "status" || option.type === "category") patch.status = normalizedLabel;
      }
    }
    return patch;
  };
  const handleRemarkChange = (lead, value) => {
    onUpdate(lead.id, { remark: value, ...inferFromRemark(value) });
  };
  const handleCallClick = (lead) => {
    if (lead.source_type === "registered_user") return;
    onUpdate(lead.id, { status: "called" });
    onCall({ ...lead, status: "called" });
  };
  const commitLeadUpdate = (lead, patch = {}) => {
    const nextLead = { ...lead, ...patch };
    const hasRemark = String(nextLead.remark || "").trim();
    const normalizedStatus = String(nextLead.status || "").trim().toLowerCase();
    const safeStatus = hasRemark
      ? (normalizedStatus && normalizedStatus !== "new" ? normalizedStatus : "remarked")
      : (normalizedStatus && normalizedStatus !== "new" ? normalizedStatus : "called");
    onUpdate(lead.id, { ...patch, status: safeStatus });
    onCall({ ...nextLead, status: safeStatus, remark: hasRemark || nextLead.remark || "" });
  };
  const applyQuickRemark = (lead, option) => {
    const value = option.label;
    const patch = { remark: value };
    if (option.type === "language") patch.language = value;
    if (option.type === "status" || option.type === "category") patch.status = value.toLowerCase();
    commitLeadUpdate(lead, patch);
  };
  return (
    <section style={dashboard}>
      <div style={dashboardHeader} className="agent-dashboard-header">
        <div>
          <h1 style={pageTitle}>Lead Calls</h1>
          <p style={muted}>Tap a phone number to call, then save call status and remark.</p>
        </div>
        <button type="button" style={refreshButton} onClick={onSearch}><RefreshCw size={24} /></button>
      </div>

      <section style={searchPanel} className="agent-search-panel">
        <div style={largeSearch}>
          <Search size={22} color="#94a3b8" />
          <input value={filters.q} onChange={(e) => onFilters((current) => ({ ...current, q: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && onSearch()} placeholder="Search name or mobile..." />
        </div>
        <div style={statusTabs} className="agent-status-tabs">
          <FilterButton label="Pending Calls" count={summary.pending || 0} active={filters.status === "pending"} onClick={() => onFilters((current) => ({ ...current, status: "pending" }))} />
          <FilterButton label="Called / Remarked" count={summary.called_records || 0} active={filters.status === "called_records"} onClick={() => onFilters((current) => ({ ...current, status: "called_records" }))} />
          <FilterButton label="Registered Users" count={summary.registered_users || 0} active={filters.status === "registered_users"} onClick={() => onFilters((current) => ({ ...current, status: "registered_users" }))} />
          <FilterButton label="All Records" count={summary.total || 0} active={filters.status === "all"} onClick={() => onFilters((current) => ({ ...current, status: "all" }))} />
          {statusOptions.slice(0, 5).map((item) => (
            <FilterButton key={item.id} label={item.label} count={statusCounts[item.label.toLowerCase()] || 0} active={filters.status === item.label.toLowerCase()} onClick={() => onFilters((current) => ({ ...current, status: item.label.toLowerCase() }))} />
          ))}
        </div>
      </section>

      <section style={leadStatsPanel} className="agent-search-panel">
        <div style={dateFilterRow}>
          <label style={dateField}><CalendarDays size={16} /><input style={dateInput} type="date" value={filters.date_from} onChange={(e) => onFilters((current) => ({ ...current, date_from: e.target.value }))} /></label>
          <label style={dateField}><CalendarDays size={16} /><input style={dateInput} type="date" value={filters.date_to} onChange={(e) => onFilters((current) => ({ ...current, date_to: e.target.value }))} /></label>
          <button type="button" style={applyDateButton} onClick={onStats}>Apply</button>
        </div>
        <div style={leadStatsNumbers}>
          <MiniProfileStat label="Calls" value={String(callStats?.totals?.total_calls || 0)} />
          <MiniProfileStat label="Numbers" value={String(callStats?.totals?.unique_leads || 0)} />
          <MiniProfileStat label="Reg. Users" value={String(callStats?.totals?.registered_users || 0)} onClick={() => onFilters((current) => ({ ...current, status: "registered_users" }))} />
          <MiniProfileStat label="Interested" value={String(callStats?.totals?.interested_count || 0)} onClick={() => onFilters((current) => ({ ...current, status: "interested" }))} />
        </div>
      </section>

      <section style={leadGrid} className="agent-lead-grid">
        {rows.map((lead) => (
          <article key={lead.id} style={isCalledLead(lead) ? calledLeadCard : leadCard}>
            <div style={leadTop}>
              <div>
                <strong style={leadName}>{lead.name || "Lead"}</strong>
                <p style={muted}>{lead.group_name || "No group"} {lead.matched_user_id ? `- User #${lead.matched_user_id}` : ""}</p>
              </div>
              <div style={leadActionRow}>
                <a style={isCalledLead(lead) ? calledLeadCallButton : leadCallButton} href={`tel:${lead.normalized_phone || lead.phone}`} onClick={() => handleCallClick(lead)}><Phone size={18} /> Call</a>
                {(filters.status !== "pending" || isCalledLead(lead)) ? (
                  <button type="button" style={leadDeleteButton} onClick={() => onDelete(lead)} title="Delete number">
                    <Trash2 size={15} />
                  </button>
                ) : null}
              </div>
            </div>
            <a style={leadPhoneLink} href={`tel:${lead.normalized_phone || lead.phone}`} data-lead-phone="true" onClick={() => handleCallClick(lead)}>{lead.phone}</a>
            <div style={leadMetaRow}>
              <span>{lead.location_hint || "India mobile"}</span>
              <span>{lead.call_count || 0} calls</span>
              <span>{formatDateTime(lead.last_called_at)}</span>
            </div>
            <div style={leadFormGrid}>
              <select style={input} value={lead.status || "new"} onChange={(e) => {
                const status = e.target.value;
                if (status === "new") onUpdate(lead.id, { status });
                else commitLeadUpdate(lead, { status });
              }}>
                <option value="new">New</option>
                {statusOptions.map((item) => <option key={item.id} value={item.label.toLowerCase()}>{item.label}</option>)}
              </select>
              <select style={input} value={lead.language || ""} onChange={(e) => onUpdate(lead.id, { language: e.target.value })}>
                <option value="">Language</option>
                {languageOptions.map((item) => <option key={item.id} value={item.label}>{item.label}</option>)}
              </select>
            </div>
            <div style={quickRemarkRow}>
              {quickRemarkOptions.map((option) => (
                <button key={option.id} type="button" style={quickRemarkChip(option.type)} onClick={() => applyQuickRemark(lead, option)}>
                  {option.label}
                </button>
              ))}
            </div>
            <input
              style={input}
              value={lead.remark || ""}
              onChange={(e) => handleRemarkChange(lead, e.target.value)}
              onBlur={(e) => {
                if (String(e.target.value || "").trim()) commitLeadUpdate(lead, { remark: e.target.value });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && String(e.currentTarget.value || "").trim()) {
                  e.currentTarget.blur();
                }
              }}
              placeholder="Remark like DND, interested, callback time..."
            />
            {lead.source_type === "registered_user" ? (
              <span style={registeredUserNote}>Registered user - tap number or Call to dial</span>
            ) : (
              <button type="button" style={leadSaveButton} onClick={() => commitLeadUpdate(lead)}><UserCheck size={17} /> Update Call</button>
            )}
          </article>
        ))}
        {!rows.length ? <p style={emptyText}>No assigned leads yet.</p> : null}
      </section>
    </section>
  );
}

function TransactionsView({ deposits, withdrawals, filters, onFilters }) {
  return (
    <section style={dashboard}>
      <div>
        <h1 style={pageTitle}>Transactions</h1>
        <p style={muted}>Deposit and withdrawal requests from your player network.</p>
      </div>
      <section style={searchPanel}>
        <div style={dateFilterRow}>
          <label style={dateField}><CalendarDays size={16} /><input style={dateInput} type="date" value={filters.date_from} onChange={(e) => onFilters((current) => ({ ...current, date_from: e.target.value }))} /></label>
          <label style={dateField}><CalendarDays size={16} /><input style={dateInput} type="date" value={filters.date_to} onChange={(e) => onFilters((current) => ({ ...current, date_to: e.target.value }))} /></label>
        </div>
        <div style={statusTabs} className="agent-status-tabs">
          <FilterButton label="All" count={(deposits.length + withdrawals.length)} active={filters.status === "all"} onClick={() => onFilters((current) => ({ ...current, status: "all" }))} />
          <FilterButton label="Pending" count={0} active={filters.status === "pending"} onClick={() => onFilters((current) => ({ ...current, status: "pending" }))} />
          <FilterButton label="Approved" count={0} active={filters.status === "approved"} onClick={() => onFilters((current) => ({ ...current, status: "approved" }))} />
          <FilterButton label="Rejected" count={0} active={filters.status === "rejected"} onClick={() => onFilters((current) => ({ ...current, status: "rejected" }))} />
        </div>
      </section>
      <div style={twoCol} className="agent-two-col">
        <RequestPanel title="Pending Deposits" rows={deposits} />
        <RequestPanel title="Pending Withdrawals" rows={withdrawals} />
      </div>
    </section>
  );
}

function RequestPanel({ title, rows }) {
  return (
    <section style={panelClean}>
      <PanelTitle icon={<Wallet size={22} />} title={title} subtitle={`${rows.length} requests`} />
      {rows.map((row) => (
        <div key={`${title}-${row.id}`} style={requestCard} className="agent-request-row">
          <div style={{ minWidth: 0 }}>
            <strong style={requestName}>{row.user_name}</strong>
            <p>{row.user_phone}</p>
            {row.remark ? <span style={requestRemark}>{row.remark}</span> : null}
          </div>
          <div style={requestAmountWrap}>
            <span style={requestAmount}>{formatMoney(row.amount)}</span>
            <span style={requestStatusPill(row.status || (title.includes("Deposit") ? "pending" : "pending"))}>{String(row.status || (title.includes("Deposit") ? "pending" : "pending")).toUpperCase()}</span>
          </div>
        </div>
      ))}
      {!rows.length && <p style={emptyText}>No pending records.</p>}
    </section>
  );
}

function AgentUserProfileModal({ state, onClose, onTab, onDate, onApply }) {
  const lists = state.data?.lists || {};
  const stats = state.data?.stats || {};
  const user = state.data?.user || {};
  const currentRows = state.tab === "dw"
    ? (lists.dw || [])
    : state.tab === "bets"
      ? (lists.bets || [])
      : state.tab === "casino"
        ? (lists.casino || [])
        : state.tab === "winnings"
          ? (lists.winnings || [])
          : (lists.bonus || []);

  return (
    <div style={overlay}>
      <section style={profileModal}>
        <div style={profileModalHeader}>
          <div>
            <strong style={profileModalTitle}>{user.name || "User Profile"}</strong>
            <p style={muted}>{user.phone || ""}</p>
          </div>
          <button type="button" style={modalClose} onClick={onClose}><X size={16} /></button>
        </div>

        <div style={profileHero} className="agent-profile-hero">
          <div style={profileIdentityCard}>
            <span style={profileAvatarLarge}>{getInitial(user.name)}</span>
            <div>
              <strong style={{ fontSize: 22 }}>{user.name || "User"}</strong>
              <div style={profileMiniMeta}>{user.agent || "Direct"} • {user.status || "Active"}</div>
            </div>
          </div>
          <div style={profileStatGrid} className="agent-profile-stats">
            <MiniProfileStat label="Deposits" value={formatMoney(stats.total_deposit)} />
            <MiniProfileStat label="Withdrawals" value={formatMoney(stats.total_withdraw)} />
            <MiniProfileStat label="Total Bets" value={String(stats.total_bets || 0)} />
            <MiniProfileStat label="Wins" value={formatMoney(stats.total_win_amount)} />
          </div>
        </div>

        <div style={profileFilterBar}>
          <div style={dateFilterRow}>
            <label style={dateField}><CalendarDays size={16} /><input style={dateInput} type="date" value={state.date_from} onChange={(e) => onDate("date_from", e.target.value)} /></label>
            <label style={dateField}><CalendarDays size={16} /><input style={dateInput} type="date" value={state.date_to} onChange={(e) => onDate("date_to", e.target.value)} /></label>
          </div>
          <button type="button" style={applyDateButton} onClick={onApply}>Apply Date</button>
        </div>

        <div style={profileTabs}>
          <button type="button" style={profileTab(state.tab === "dw")} onClick={() => onTab("dw")}>D/W</button>
          <button type="button" style={profileTab(state.tab === "bets")} onClick={() => onTab("bets")}>Bets</button>
          <button type="button" style={profileTab(state.tab === "casino")} onClick={() => onTab("casino")}>Casino</button>
          <button type="button" style={profileTab(state.tab === "winnings")} onClick={() => onTab("winnings")}>Winnings</button>
          <button type="button" style={profileTab(state.tab === "bonus")} onClick={() => onTab("bonus")}>Bonus</button>
        </div>

        <div style={profileListArea}>
          {state.loading ? <p style={emptyText}>Loading profile...</p> : currentRows.length ? currentRows.map((row, index) => (
            <div key={`${state.tab}-${row.id || index}`} style={historyRowCard}>
              <div>
                <strong style={historyRowTitle}>{state.tab === "bets" ? (row.market_name || row.type || "Bet") : state.tab === "casino" ? (row.game_name || "Casino") : state.tab === "bonus" ? (row.ref_type || "Bonus") : (row.type || "Entry")}</strong>
                <p style={historyRowMeta}>{formatDateTime(row.created_at || row.settled_at)} {row.status ? `• ${row.status}` : ""}</p>
              </div>
              <div style={historyRowValueWrap}>
                <strong style={historyRowValue}>
                  {state.tab === "bets"
                    ? formatMoney(row.amount || row.bid_amount)
                    : state.tab === "winnings"
                      ? formatMoney(row.win_amount)
                      : state.tab === "casino"
                        ? formatMoney(row.win_amount || row.bet_amount)
                        : formatMoney(row.amount || 0)}
                </strong>
                {row.payout || row.win_amount ? <span style={historyRowSub}>Win {formatMoney(row.payout || row.win_amount)}</span> : null}
              </div>
            </div>
          )) : <p style={emptyText}>No records for selected date.</p>}
        </div>
      </section>
    </div>
  );
}

function MiniProfileStat({ label, value, onClick }) {
  return (
    <button type="button" style={onClick ? miniProfileStatButton : miniProfileStat} onClick={onClick || undefined}>
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

function BigStat({ title, value, foot, color, icon }) {
  return (
    <article style={{ ...bigStat, background: `linear-gradient(135deg, ${color}, ${shade(color)})` }}>
      <div style={bigStatBody}>
        <span style={bigStatLabel}>{title}</span>
        <strong style={bigStatValue}>{value}</strong>
        <p style={bigStatFoot}>{foot}</p>
      </div>
      <div style={bigStatIcon}>{icon}</div>
    </article>
  );
}

function PanelTitle({ icon, title, subtitle }) {
  return (
    <div style={panelTitle}>
      <span style={panelIcon}>{icon}</span>
      <div>
        <h3>{title}</h3>
        <p>{subtitle}</p>
      </div>
    </div>
  );
}

function MoneyBox({ label, value, tone, wide }) {
  return <div style={{ ...moneyBox, ...(wide ? { gridColumn: "1 / -1" } : null), ...(tone === "green" ? greenBox : redBox) }}><span style={moneyBoxLabel}>{label}</span><strong style={moneyBoxValue}>{value}</strong></div>;
}

function ActionCard({ title, subtitle, color, bg, icon, onClick }) {
  return (
    <button type="button" style={{ ...actionCard, background: bg, borderColor: `${color}44`, color }} onClick={onClick}>
      {icon}
      <strong>{title}</strong>
      <span>{subtitle}</span>
    </button>
  );
}

function FilterButton({ label, count, active, onClick }) {
  return <button type="button" style={active ? activeFilter : filterButton} onClick={onClick}>{label} <span>{count}</span></button>;
}

function TopMetric({ label, value, green }) {
  return <div style={topMetric}><span>{label}</span><strong style={green ? greenText : null}>{value}</strong></div>;
}

function Modal({ title, children, onClose, onSave, saveLabel = "Save" }) {
  return (
    <div style={overlay}>
      <section style={modal}>
        <div style={modalHeader}>
          <strong>{title}</strong>
          <button type="button" style={modalClose} onClick={onClose}>x</button>
        </div>
        <div style={{ display: "grid", gap: 12 }}>{children}</div>
        <div style={modalFooter}>
          <button type="button" style={cancelButton} onClick={onClose}>Cancel</button>
          <button type="button" style={saveButton} onClick={onSave}>{saveLabel}</button>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return <label style={field}><span>{label}</span>{children}</label>;
}

function getInitial(value) {
  return String(value || "A").slice(0, 1).toUpperCase();
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function formatCompactMoney(value) {
  const amount = Number(value || 0);
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount);
  if (abs >= 100000) return `${sign}Rs ${(abs / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `${sign}Rs ${(abs / 1000).toFixed(1)}K`;
  return `${sign}Rs ${abs.toFixed(0)}`;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isCalledLead(lead) {
  const status = String(lead?.status || "").toLowerCase();
  return Boolean(
    lead?.last_called_at ||
    String(lead?.remark || "").trim() ||
    (status && status !== "new" && status !== "pending")
  );
}

function shade(color) {
  const map = {
    "#8b5cf6": "#7c3aed",
    "#10b981": "#059669",
    "#f97316": "#ea580c",
    "#2563eb": "#1d4ed8",
  };
  return map[color] || color;
}

const page = { minHeight: "100vh", background: "#f8fafc", color: "#0f172a" };
const topbar = { height: 80, background: "rgba(255,255,255,.95)", borderBottom: "1px solid #e5e7eb", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, position: "sticky", top: 0, zIndex: 20, backdropFilter: "blur(14px)" };
const topbarBrand = { display: "flex", alignItems: "center", gap: 14, minWidth: 0 };
const sidebarToggleButton = { minHeight: 40, border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff", color: "#0f172a", padding: "0 12px", display: "inline-flex", alignItems: "center", gap: 7, fontWeight: 900, boxShadow: "0 8px 18px rgba(15,23,42,.06)" };
const topbarLogo = { width: 44, height: 44, borderRadius: 14, objectFit: "cover", boxShadow: "0 10px 24px rgba(225,29,72,.18)" };
const welcome = { margin: 0, fontSize: 18, fontWeight: 900 };
const muted = { margin: "4px 0 0", color: "#64748b", fontSize: 14 };
const topbarRight = { display: "flex", alignItems: "center", gap: 18 };
const topMetric = { textAlign: "right", textTransform: "uppercase", fontSize: 12, color: "#64748b", display: "grid", gap: 4 };
const bellButton = { width: 38, height: 38, border: 0, background: "transparent", position: "relative", display: "grid", placeItems: "center" };
const dot = { position: "absolute", top: 7, right: 7, width: 10, height: 10, borderRadius: 99, background: "#ef4444" };
const profileButton = { width: 42, height: 42, border: 0, background: "linear-gradient(135deg,#2563eb,#9333ea)", color: "#fff", fontWeight: 900, fontSize: 18 };
const logoutButton = { minHeight: 40, border: "1px solid #fecdd3", background: "#fff1f2", color: "#be123c", borderRadius: 12, padding: "0 14px", display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 800 };
const main = { width: "min(1800px, 100%)", margin: "0 auto", padding: "28px 28px 56px", display: "grid", gridTemplateColumns: "280px minmax(0, 1fr)", gap: 24, alignItems: "start" };
const mainFull = { ...main, gridTemplateColumns: "minmax(0, 1fr)" };
const sidebar = { position: "sticky", top: 104, borderRadius: 28, padding: 18, background: "linear-gradient(180deg, #141720, #10131b)", border: "1px solid rgba(255,255,255,.06)", boxShadow: "0 24px 60px rgba(15,23,42,.18)", color: "#fff", display: "grid", gap: 18 };
const sidebarHead = { display: "flex", alignItems: "center", gap: 12, paddingBottom: 8, borderBottom: "1px solid rgba(255,255,255,.07)" };
const sidebarCloseButton = { marginLeft: "auto", width: 32, height: 32, border: "1px solid rgba(255,255,255,.1)", borderRadius: 10, background: "rgba(255,255,255,.06)", color: "#fff", fontSize: 20, lineHeight: 1, cursor: "pointer" };
const sidebarLogo = { width: 46, height: 46, borderRadius: 14, objectFit: "cover" };
const sidebarTitle = { display: "block", fontSize: 17, fontWeight: 900 };
const sidebarText = { margin: "2px 0 0", color: "rgba(255,255,255,.58)", fontSize: 12 };
const sidebarNav = { display: "grid", gap: 10 };
const sideNavButton = (active) => ({ minHeight: 48, borderRadius: 16, border: active ? "1px solid rgba(255,255,255,.06)" : "1px solid transparent", background: active ? "linear-gradient(135deg, #ff2446, #d9153a)" : "rgba(255,255,255,.03)", color: "#fff", display: "flex", alignItems: "center", gap: 10, padding: "0 14px", fontWeight: 800, textAlign: "left", boxShadow: active ? "0 14px 24px rgba(217,21,58,.22)" : "none" });
const sidebarFoot = { marginTop: 8, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,.07)", display: "grid", gap: 6 };
const sidebarAgentName = { fontSize: 15, fontWeight: 900 };
const sidebarAgentMeta = { color: "rgba(255,255,255,.56)", fontSize: 12, lineHeight: 1.5 };
const sidebarLogout = { marginTop: 6, minHeight: 44, borderRadius: 14, border: "1px solid rgba(255,255,255,.08)", background: "rgba(255,255,255,.04)", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, fontWeight: 800 };
const dashboard = { display: "grid", gap: 20 };
const dashboardHeader = { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 };
const pageTitle = { margin: 0, fontSize: 28, fontWeight: 950 };
const liveBadge = { marginLeft: 8, fontSize: 12, color: "#16a34a", border: "1px solid #bbf7d0", background: "#f0fdf4", borderRadius: 999, padding: "4px 10px", verticalAlign: "middle" };
const dashboardTools = { display: "flex", alignItems: "center", gap: 18 };
const todayCard = { minHeight: 66, padding: "0 22px", borderRadius: 14, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", display: "grid", gridTemplateColumns: "auto 1fr", alignItems: "center", gap: "0 10px", fontWeight: 900 };
const refreshButton = { border: 0, background: "transparent", color: "#64748b", display: "grid", placeItems: "center" };
const statGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16 };
const bigStat = { minHeight: 170, borderRadius: 16, color: "#fff", padding: 24, display: "flex", justifyContent: "space-between", boxShadow: "0 14px 30px rgba(15,23,42,0.12)" };
const bigStatBody = { display: "grid", alignContent: "space-between" };
const bigStatLabel = { fontSize: 14, fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase", opacity: 0.92 };
const bigStatValue = { fontSize: 26, lineHeight: 1.05, fontWeight: 900, letterSpacing: "-0.03em" };
const bigStatFoot = { margin: 0, fontSize: 13, opacity: 0.9 };
const bigStatIcon = { width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,.18)", display: "grid", placeItems: "center" };
const percentIcon = { fontSize: 28, fontWeight: 900 };
const dollarIcon = { fontSize: 34 };
const twoCol = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 };
const panel = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 18, padding: 24, boxShadow: "0 8px 20px rgba(15,23,42,0.04)" };
const panelClean = { ...panel, borderRadius: 24, boxShadow: "0 12px 28px rgba(15,23,42,.06)" };
const panelTitle = { display: "flex", alignItems: "center", gap: 14, marginBottom: 24 };
const panelIcon = { width: 44, height: 44, background: "linear-gradient(180deg, #f8fafc, #eef2ff)", borderRadius: 14, display: "grid", placeItems: "center", color: "#64748b" };
const moneyGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 };
const moneyBox = { minHeight: 92, borderRadius: 18, padding: 18, display: "grid", gap: 10, fontWeight: 900 };
const moneyBoxLabel = { fontSize: 13, fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase" };
const moneyBoxValue = { fontSize: 24, lineHeight: 1.08, fontWeight: 900, letterSpacing: "-0.03em" };
const greenBox = { background: "#ecfdf5", color: "#059669", border: "1px solid #bbf7d0" };
const redBox = { background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" };
const actionsGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 };
const actionCard = { minHeight: 132, border: "1px solid", borderRadius: 14, padding: 22, display: "grid", justifyItems: "start", alignContent: "center", gap: 8, textAlign: "left", fontSize: 16, cursor: "pointer" };
const wideAction = { gridColumn: "1 / -1", minHeight: 80, borderRadius: 14, border: "1px solid #fed7aa", background: "#fff7ed", color: "#ea580c", fontWeight: 900, display: "flex", alignItems: "center", gap: 14, padding: "0 22px", fontSize: 16 };
const topUserList = { display: "grid", gap: 12 };
const topUserRow = { display: "flex", alignItems: "center", gap: 16, minHeight: 64 };
const rankBadge = (index) => ({ width: 42, height: 42, background: index === 0 ? "#eab308" : index === 1 ? "#9ca3af" : "#ea580c", color: "#fff", display: "grid", placeItems: "center", fontWeight: 900 });
const rightMoney = { textAlign: "right", display: "grid", gap: 4, color: "#16a34a" };
const viewUsersButton = { marginTop: 10, minHeight: 44, justifySelf: "start", border: 0, borderRadius: 10, background: "#2563eb", color: "#fff", fontWeight: 900, padding: "0 18px" };
const searchPanel = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 22, display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "center" };
const largeSearch = { minHeight: 54, border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 14, padding: "0 16px" };
const statusTabs = { display: "flex", alignItems: "center", gap: 8, background: "#f3f4f6", padding: 5 };
const filterButton = { minHeight: 42, border: 0, background: "transparent", color: "#475569", fontWeight: 800, padding: "0 14px" };
const activeFilter = { ...filterButton, background: "#fff", color: "#2563eb" };
const downloadButton = { width: 50, height: 50, border: "1px solid #e5e7eb", background: "#fff", color: "#64748b" };
const createUserButton = { minHeight: 54, border: 0, borderRadius: 12, padding: "0 24px", background: "linear-gradient(135deg,#2563eb,#9333ea)", color: "#fff", fontWeight: 900, display: "flex", alignItems: "center", gap: 8, boxShadow: "0 10px 24px rgba(37,99,235,.22)" };
const userTablePanel = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, overflow: "auto" };
const userTable = { width: "100%", borderCollapse: "collapse" };
const userCell = { display: "flex", alignItems: "center", gap: 14 };
const clientAvatar = { width: 48, height: 48, borderRadius: 999, background: "linear-gradient(135deg,#2563eb,#9333ea)", color: "#fff", display: "grid", placeItems: "center", fontWeight: 900 };
const greenText = { color: "#059669", fontWeight: 900 };
const redText = { color: "#dc2626", fontWeight: 900 };
const smallMuted = { margin: "4px 0 0", color: "#64748b", fontSize: 12 };
const statusPill = (status) => ({ display: "inline-flex", minHeight: 26, alignItems: "center", padding: "0 12px", borderRadius: 999, background: "#dcfce7", color: "#059669", fontWeight: 900 });
const profileButtonSmall = { minHeight: 38, border: 0, background: "#10b981", color: "#fff", padding: "0 16px", fontWeight: 900, display: "inline-flex", alignItems: "center", gap: 8 };
const leadGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 };
const leadCard = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12, display: "grid", gap: 8, boxShadow: "0 6px 14px rgba(15,23,42,.04)" };
const calledLeadCard = { ...leadCard, borderColor: "#f59e0b", background: "linear-gradient(135deg,#fffbeb,#fff)", boxShadow: "0 8px 18px rgba(217,119,6,.12)" };
const leadTop = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 };
const leadName = { display: "block", fontSize: 16, fontWeight: 900, color: "#0f172a" };
const leadActionRow = { display: "inline-flex", alignItems: "center", gap: 7, flex: "0 0 auto" };
const leadCallButton = { minHeight: 38, borderRadius: 999, background: "#dcfce7", color: "#15803d", padding: "0 12px", display: "inline-flex", alignItems: "center", gap: 7, textDecoration: "none", fontWeight: 950, flex: "0 0 auto" };
const calledLeadCallButton = { ...leadCallButton, background: "linear-gradient(135deg,#fde68a,#f59e0b)", color: "#78350f", boxShadow: "0 8px 18px rgba(217,119,6,.24)" };
const leadDeleteButton = { width: 38, height: 38, border: 0, borderRadius: 999, background: "#fee2e2", color: "#dc2626", display: "grid", placeItems: "center" };
const leadPhone = { fontSize: 20, fontWeight: 950, color: "#111827", letterSpacing: ".02em" };
const leadPhoneLink = { ...leadPhone, display: "block", textDecoration: "none" };
const leadMetaRow = { display: "flex", flexWrap: "wrap", gap: 6, fontSize: 12, color: "#64748b" };
const leadFormGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 };
const leadStatsPanel = { ...searchPanel, gridTemplateColumns: "auto 1fr" };
const leadStatsNumbers = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 };
const quickRemarkRow = { display: "flex", gap: 7, overflowX: "auto", paddingBottom: 2 };
const quickRemarkChip = (type) => ({ minHeight: 32, flex: "0 0 auto", border: "1px solid #e5e7eb", borderRadius: 999, background: type === "language" ? "#eff6ff" : "#fff7ed", color: type === "language" ? "#2563eb" : "#c2410c", padding: "0 10px", fontWeight: 900, fontSize: 12 });
const leadSaveButton = { minHeight: 40, border: 0, borderRadius: 12, background: "linear-gradient(135deg,#2563eb,#7c3aed)", color: "#fff", fontWeight: 950, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 };
const registeredUserNote = { minHeight: 40, borderRadius: 12, background: "#eff6ff", color: "#1d4ed8", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 10px", fontSize: 12, fontWeight: 950 };
const requestCard = { minHeight: 88, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "16px 0", borderBottom: "1px solid #eef2f7" };
const requestName = { display: "block", fontSize: 16, fontWeight: 800, color: "#0f172a" };
const requestRemark = { display: "inline-flex", marginTop: 8, padding: "5px 10px", borderRadius: 999, background: "#f8fafc", color: "#64748b", fontSize: 11, fontWeight: 700 };
const requestAmountWrap = { display: "grid", justifyItems: "end", gap: 6, flex: "0 0 auto" };
const requestAmount = { fontSize: 20, fontWeight: 900, color: "#0f172a", letterSpacing: "-0.02em" };
const requestStatusPill = (status) => {
  const tone = String(status || "pending").toLowerCase();
  return {
    minHeight: 26,
    padding: "0 10px",
    borderRadius: 999,
    background: tone === "approved" ? "#dcfce7" : tone === "rejected" ? "#fee2e2" : "#eef2ff",
    color: tone === "approved" ? "#15803d" : tone === "rejected" ? "#dc2626" : "#4f46e5",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".04em",
  };
};
const emptyText = { color: "#64748b" };
const overlay = { position: "fixed", inset: 0, zIndex: 999, background: "rgba(15,23,42,.45)", display: "grid", placeItems: "center", padding: 18 };
const modal = { width: 460, maxWidth: "94vw", background: "#fff", borderRadius: 16, padding: 20, display: "grid", gap: 16 };
const profileModal = { width: "min(1180px, 96vw)", maxHeight: "92vh", overflow: "hidden", background: "#fff", borderRadius: 24, padding: 22, display: "grid", gap: 16 };
const profileModalHeader = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 };
const profileModalTitle = { display: "block", fontSize: 24, fontWeight: 900 };
const profileHero = { display: "grid", gridTemplateColumns: "minmax(260px, .8fr) minmax(0, 1.2fr)", gap: 16 };
const profileIdentityCard = { background: "linear-gradient(135deg, #111827, #1f2937)", color: "#fff", borderRadius: 22, padding: 18, display: "flex", alignItems: "center", gap: 16 };
const profileAvatarLarge = { width: 64, height: 64, borderRadius: 18, background: "linear-gradient(135deg,#2563eb,#9333ea)", color: "#fff", display: "grid", placeItems: "center", fontSize: 24, fontWeight: 900 };
const profileMiniMeta = { marginTop: 4, color: "rgba(255,255,255,.7)", fontSize: 13 };
const profileStatGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 };
const miniProfileStat = { background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 18, padding: 16, display: "grid", gap: 8, textAlign: "left", color: "#0f172a" };
const miniProfileStatButton = { ...miniProfileStat, cursor: "pointer", boxShadow: "0 8px 18px rgba(37,99,235,.10)" };
const profileFilterBar = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" };
const dateFilterRow = { display: "flex", gap: 10, flexWrap: "wrap" };
const dateField = { minHeight: 44, padding: "0 12px", borderRadius: 14, border: "1px solid #dbe4f0", display: "inline-flex", alignItems: "center", gap: 8, background: "#fff" };
const dateInput = { border: 0, outline: "none", font: "inherit", color: "#0f172a", background: "transparent" };
const applyDateButton = { minHeight: 44, border: 0, borderRadius: 14, padding: "0 16px", background: "#111827", color: "#fff", fontWeight: 800 };
const profileTabs = { display: "flex", gap: 10, flexWrap: "wrap" };
const profileTab = (active) => ({ minHeight: 40, borderRadius: 999, border: active ? "1px solid #dbeafe" : "1px solid #e5e7eb", background: active ? "#eff6ff" : "#fff", color: active ? "#2563eb" : "#475569", padding: "0 14px", fontWeight: 800 });
const profileListArea = { minHeight: 220, maxHeight: "42vh", overflowY: "auto", paddingRight: 4, display: "grid", gap: 10 };
const historyRowCard = { border: "1px solid #eef2f7", borderRadius: 18, padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 };
const historyRowTitle = { display: "block", fontSize: 15, fontWeight: 800, color: "#0f172a" };
const historyRowMeta = { margin: "4px 0 0", color: "#64748b", fontSize: 12 };
const historyRowValueWrap = { textAlign: "right", display: "grid", gap: 4 };
const historyRowValue = { fontSize: 16, fontWeight: 900, color: "#0f172a" };
const historyRowSub = { color: "#16a34a", fontSize: 12, fontWeight: 700 };
const modalHeader = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const modalClose = { border: 0, background: "#f1f5f9", width: 32, height: 32 };
const modalFooter = { display: "flex", justifyContent: "flex-end", gap: 10 };
const cancelButton = { minHeight: 42, border: "1px solid #e5e7eb", background: "#fff", padding: "0 16px", fontWeight: 800 };
const saveButton = { minHeight: 42, border: 0, background: "#2563eb", color: "#fff", padding: "0 18px", fontWeight: 900 };
const field = { display: "grid", gap: 8, color: "#475569", fontWeight: 800 };
const input = { minHeight: 46, border: "1px solid #dbe4f0", padding: "0 12px" };
const fileButton = { minHeight: 46, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontWeight: 900 };
const agentPanelCss = `
  .agent-users-table th {
    text-align: left;
    padding: 20px 22px;
    color: #64748b;
    font-size: 12px;
    text-transform: uppercase;
    background: #f8fafc;
    border-bottom: 1px solid #eef2f7;
  }
  .agent-users-table td {
    padding: 18px 22px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }
  .agent-users-table p,
  .agent-top-list p,
  .agent-request-row p {
    margin: 3px 0 0;
    color: #64748b;
  }
  @media (max-width: 1100px) {
    .agent-main {
      grid-template-columns: 1fr !important;
    }
    .agent-sidebar {
      position: relative !important;
      top: 0 !important;
    }
    .agent-stat-grid,
    .agent-two-col {
      grid-template-columns: 1fr !important;
    }
    .agent-search-panel {
      grid-template-columns: 1fr !important;
    }
    .agent-profile-hero,
    .agent-profile-stats {
      grid-template-columns: 1fr !important;
    }
    .agent-lead-grid {
      grid-template-columns: 1fr !important;
    }
  }
  @media (max-width: 720px) {
    .agent-topbar {
      height: auto !important;
      padding: 14px !important;
      align-items: flex-start !important;
      flex-direction: column;
    }
    .agent-sidebar {
      padding: 14px !important;
    }
    .agent-main {
      padding: 22px 12px 42px !important;
    }
    .agent-dashboard-header {
      flex-direction: column;
    }
    .agent-status-tabs {
      overflow-x: auto;
      width: 100%;
    }
    .agent-lead-grid article {
      padding: 10px !important;
      border-radius: 12px !important;
      box-shadow: none !important;
    }
    .agent-lead-grid article [data-lead-phone="true"] {
      font-size: 18px !important;
      line-height: 1.1 !important;
    }
  }
`;

export default AgentPanel;
